/**
 * standings-loader.ts - Standings Data Loading Functions
 *
 * Handles fetching and loading standings data from the API
 * including standings, rankings, and round-specific data.
 */

import { TOURNAMENT_FORMATS, CSS_CLASSES } from '../../utils/constants';
import { fetchStandings, fetchRanking, fetchTournamentById } from '../../api/tournament-api';
import { setStandingsViewMode } from './standings-renderer';

/**
 * Load categories for a tournament
 */
async function loadCategoriesForStandings(tournamentId: string): Promise<any[]> {
  try {
    const response = await fetch(`/api/tournaments/${tournamentId}/categories`, { credentials: 'include' });
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.warn('Could not fetch categories:', e);
  }
  return [];
}

/**
 * Load standings for a tournament
 * @param tournamentId - The tournament ID
 */
export async function loadStandings(tournamentId: string): Promise<void> {
  const standingsDiv = document.getElementById(`standings-${tournamentId}`);
  if (!standingsDiv) return;

  // Only show loading for initial load (when div is empty)
  if (!standingsDiv.innerHTML || standingsDiv.innerHTML.trim() === '') {
    standingsDiv.innerHTML = '<p>Loading standings...</p>';
    // Reset view mode on initial load
    setStandingsViewMode(tournamentId, 'standings');
  }
  // Otherwise, don't clear content to avoid flash when refreshing

  try {
    const [data, categories] = await Promise.all([
      fetchStandings(tournamentId),
      loadCategoriesForStandings(tournamentId)
    ]);

    // For knockout and round robin tournaments, fetch matches separately since standings endpoint might not include them
    if (data.tournament.format === TOURNAMENT_FORMATS.KNOCKOUT || data.tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      try {
        const tournamentData = await fetchTournamentById(tournamentId);
        data.tournament.matches = tournamentData.matches;
      } catch (e) {
        console.warn('Could not fetch tournament matches:', e);
      }
    }

    if (window.renderStandings) {
      standingsDiv.innerHTML = window.renderStandings(data.standings, data.tournament, tournamentId, null, categories, null);
    }
  } catch (error) {
    console.error('Error loading standings:', error);
    standingsDiv.innerHTML = '<p class="error">Error loading standings</p>';
  }
}

/**
 * Load standings for a specific round
 * @param tournamentId - The tournament ID
 * @param round - The round number
 */
export async function loadStandingsForRound(tournamentId: string, round: number): Promise<void> {
  const standingsDiv = document.getElementById(`standings-${tournamentId}`);
  if (!standingsDiv) return;

  // Reset view mode to standings when selecting a round
  setStandingsViewMode(tournamentId, 'standings');

  // Don't show "Loading..." message to avoid flash - just update directly
  // The update is usually fast enough that showing loading state causes more flicker

  try {
    const [data, categories] = await Promise.all([
      fetchStandings(tournamentId, round),
      loadCategoriesForStandings(tournamentId)
    ]);

    // For knockout and round robin tournaments, fetch matches separately since standings endpoint might not include them
    if (data.tournament.format === TOURNAMENT_FORMATS.KNOCKOUT || data.tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN) {
      try {
        const tournamentData = await fetchTournamentById(tournamentId);
        data.tournament.matches = tournamentData.matches;
      } catch (e) {
        console.warn('Could not fetch tournament matches:', e);
      }
    }

    if (window.renderStandings) {
      standingsDiv.innerHTML = window.renderStandings(data.standings, data.tournament, tournamentId, round, categories, null);
    }
  } catch (error) {
    console.error('Error loading standings:', error);
    standingsDiv.innerHTML = '<p class="error">Error loading standings</p>';
  }
}

/**
 * Load ranking in standings view
 * @param tournamentId - The tournament ID
 */
export async function loadRankingInStandings(tournamentId: string): Promise<void> {
  const standingsDiv = document.getElementById(`standings-${tournamentId}`);
  if (!standingsDiv) return;

  // Don't show loading message to avoid flash
  // standingsDiv.innerHTML = '<p>Loading ranking...</p>';

  try {
    // First, fetch tournament to check format
    const tournament = await fetchTournamentById(tournamentId);
    if (!tournament) {
      throw new Error('Failed to load tournament');
    }

    // Only load ranking for Swiss tournaments
    if (tournament.format !== TOURNAMENT_FORMATS.SWISS) {
      standingsDiv.innerHTML = '<p class="error">Initial ranking is only available for Swiss tournaments.</p>';
      return;
    }

    // Fetch ranking data
    const ranking = await fetchRanking(tournamentId);
    if (!ranking || !Array.isArray(ranking)) {
      throw new Error('Failed to load ranking');
    }

    if (window.renderRankingInStandings) {
      standingsDiv.innerHTML = window.renderRankingInStandings(ranking, tournament, tournamentId);
    }

    // Update button states
    const allButtons = document.querySelectorAll('.standings-round-selector .btn-small');
    allButtons.forEach(btn => {
      const buttonElement = btn as HTMLElement;
      if (buttonElement.getAttribute('data-type') === 'ranking') {
        buttonElement.classList.remove(CSS_CLASSES.BTN_SECONDARY);
        buttonElement.classList.add(CSS_CLASSES.BTN_PRIMARY);
      } else if (!(btn as HTMLButtonElement).onclick) { // Don't change print button
        buttonElement.classList.remove(CSS_CLASSES.BTN_PRIMARY);
        buttonElement.classList.add(CSS_CLASSES.BTN_SECONDARY);
      }
    });
  } catch (error) {
    console.error('Error loading ranking:', error);
    standingsDiv.innerHTML = '<p class="error">Error loading ranking. Initial ranking is only available for Swiss tournaments.</p>';
  }
}

// Export to window for global access
window.loadStandings = loadStandings;
window.loadStandingsForRound = loadStandingsForRound;
window.loadRankingInStandings = loadRankingInStandings;
